import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { App } from "antd";
import type { FirebaseError } from "firebase/app";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { DIT_LOGO_URL, DIT_LOGO_ALT } from "../lib/dit";

function mapFirebaseError(err: unknown): string {
  const code = (err as FirebaseError)?.code;
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password";
    case "auth/too-many-requests":
      return "Too many attempts — try again later";
    case "auth/user-disabled":
      return "Your account is disabled — contact an admin";
    default:
      return (err as Error)?.message || "Sign-in failed";
  }
}

function ForgotPasswordModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { sendPasswordReset } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ email: string }>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: { email: string }) => {
    setSubmitting(true);
    try {
      await sendPasswordReset(values.email.trim().toLowerCase());
      message.success("Check your email for reset instructions");
      form.resetFields();
      onClose();
    } catch (err) {
      message.error(mapFirebaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Reset your password"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
        >
          <Input placeholder="you@nc.gov" autoComplete="email" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting} block>
          Send reset link
        </Button>
      </Form>
    </Modal>
  );
}

export default function SignIn() {
  const { signIn, signInWithSso, user, isExec } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm<{ email: string; password: string }>();

  useEffect(() => {
    if (user) {
      // OAIP-48: executives land on the exec dashboard; operators land on `/`.
      navigate(isExec ? "/exec" : "/");
    }
  }, [user, isExec, navigate]);

  const handleSignIn = async (values: { email: string; password: string }) => {
    try {
      setError(null);
      await signIn(values.email.trim().toLowerCase(), values.password);
    } catch (err) {
      setError(mapFirebaseError(err));
    }
  };

  return (
    <App>
      <div
        style={{
          width: "100%",
          minHeight: "calc(100vh - 60px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          // Background lives on Layout.Content (App.tsx) so the iridescent
          // dashboard image shows through. Just center the card here.
          background: "transparent",
        }}
      >
        <Card
          style={{
            width: 420,
            // Soft glassy lift over the iridescent background.
            background: "rgba(255, 255, 255, 0.92)",
            backdropFilter: "saturate(140%) blur(8px)",
            WebkitBackdropFilter: "saturate(140%) blur(8px)",
            boxShadow:
              "0 1px 2px rgba(9, 41, 64, 0.04), 0 12px 40px rgba(9, 41, 64, 0.12)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Space direction="vertical" size="small" style={{ width: "100%", alignItems: "center" }}>
              <img
                src={DIT_LOGO_URL}
                alt={DIT_LOGO_ALT}
                style={{ height: 48, display: "block", margin: "0 auto 8px" }}
              />
              <Typography.Title
                level={3}
                style={{ textAlign: "center", marginBottom: 0, color: "#092940" }}
              >
                Secure Workspace
              </Typography.Title>
              <Typography.Text
                type="secondary"
                style={{ textAlign: "center", display: "block" }}
              >
                Sign in to access project tracking, AI summaries, and governance workflows.
              </Typography.Text>
            </Space>

            {error && <Alert type="error" message={error} showIcon />}

            <Form form={form} layout="vertical" onFinish={handleSignIn}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
              >
                <Input placeholder="you@nc.gov" autoComplete="username" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password placeholder="Password" autoComplete="current-password" />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                style={{ background: "#092940", borderColor: "#092940" }}
              >
                Sign in
              </Button>
            </Form>

            <Button
              type="link"
              onClick={() => setForgotOpen(true)}
              style={{ padding: 0 }}
              aria-label="Open forgot password dialog"
            >
              Forgot password?
            </Button>

            <Divider>or</Divider>

            <Tooltip title="Coming soon — Entra ID SSO integration">
              <Button
                block
                disabled
                onClick={() => signInWithSso("saml")}
                aria-label="SSO sign-in, coming soon"
              >
                Continue with SSO (Entra ID)
              </Button>
            </Tooltip>
          </Space>
        </Card>

        <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
      </div>
    </App>
  );
}
